import React, { useState } from "react";
import { Box, Button, Flex, Text, useDisclosure } from "@chakra-ui/react";
import { Icon, useToast } from "@familyzone/component-library";
import { colours, toasts } from "utils/themeUtils";
import { deleteSavedFocusFromLibrary } from "js/api/focus";
import { ConfirmDeleteFocusModal } from "../ConfirmDeleteFocusModal";
import { useFocusStore } from "stores/v2/focusStore";
import { useUserSessionStore } from "stores/v2/userSessionStore";

export interface Props {
	id: string;
	name: string;
	/**
	 * Whether the focus is currently applied to any ACTIVE classes.
	 */
	active: boolean;
	handleClickFocusItem: () => void;
	hideDeleteOption: boolean;
}

export function FocusLibraryItem(props: Props): JSX.Element {
	const toast = useToast();
	const [isDeleting, setIsDeleting] = useState(false);
	const confirmationModal = useDisclosure();

	const focusStore = useFocusStore();

	function handleConfirmDelete() {
		setIsDeleting(true);
		deleteSavedFocusFromLibrary(props.id, useUserSessionStore.getState().getTeacherId() as string)
			.then(() => {
				focusStore.removeFocusItemFromLibrary(props.id);
				confirmationModal.onClose();
				toast.successToast({
					title: "Focus deleted",
					description: `"${props.name}" focus has been successfully deleted from your Focus Library.`,
					position: toasts.position,
					isClosable: true,
				});
			})
			.catch(() => {
				toast.errorToast({
					title: "Failed to delete Focus",
					description: `There was an error deleting your saved focus "${props.name}" from your Focus Library.`,
					position: toasts.position,
					isClosable: true,
				});
			})
			.finally(() => {
				setIsDeleting(false);
			});
	}

	function handleClickFocusItem() {
		props.handleClickFocusItem();
	}

	const px = 15;
	const py = 10;

	return (
		<>
			<Flex justifyContent={"space-between"} alignItems={"center"} background={colours.blue[500]} mb={8} rounded={"sm"}>
				<Box
					as="button"
					flex={1}
					px={px}
					py={py}
					onClick={handleClickFocusItem}
					fontWeight={"light"}
					color={colours.gray[800]}
					textAlign={"left"}
				>
					{props.name}
				</Box>

				{props.active && (
					<Text bg={"white"} fontSize={"sm"} rounded={"lg"} px={8} py={3} mr={8} data-testid="active-focus-label">
						Active
					</Text>
				)}

				{!props.active && !props.hideDeleteOption && (
					<Button variant={"ghost"} px={px} py={py} onClick={() => confirmationModal.onOpen()} data-testid="delete-focus-button">
						<Icon icon={"fa-trash-can"} fontSize={"sm"} cursor={"pointer"} color={colours.gray[800]} />
					</Button>
				)}
			</Flex>
			<ConfirmDeleteFocusModal
				name={props.name}
				isDeleting={isDeleting}
				isOpen={confirmationModal.isOpen}
				closeModal={confirmationModal.onClose}
				handleConfirmDelete={handleConfirmDelete}
			/>
		</>
	);
}
